import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { Modal } from "@/components/design/Modal";
import type { TobisAdmissions } from "@/lib/tobis";
import { marketLabel } from "./market-label";

// Die Kinobesucher-Kennzahl der Titelseite aufgeklappt: eine Zeile je Markt,
// absteigend nach Zuschauern.
//
// Warum ueberhaupt aufklappen: die Summe allein sagt nicht, wie sie zustande
// kommt. 60 Millionen aus fuenf Maerkten ist eine andere Aussage als dieselbe
// Summe aus 35 - fuer eine Einkaufsentscheidung ist die Verteilung der
// eigentliche Inhalt.

interface TobisAdmissionsModalProps {
  open: boolean;
  onClose: () => void;
  titleName: string;
  admissions: TobisAdmissions;
  /**
   * Die DE-Zahl der FFA-Hitliste, nur wenn sie vorliegt: sie ist eine
   * veroeffentlichte Abrechnung und weicht deshalb von LUMIEREs DE-Zeile ab.
   * Beide Zahlen stehen getrennt nebeneinander - sie zu verrechnen erzeugte
   * eine dritte, die in keiner Quelle steht.
   */
  ffaAdmissionsDe?: string | null;
}

export function TobisAdmissionsModal({
  open,
  onClose,
  titleName,
  admissions,
  ffaAdmissionsDe = null,
}: TobisAdmissionsModalProps) {
  const intl = useIntl();
  const number = (value: string) => intl.formatNumber(Number(value));

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth="34rem"
      title={intl.formatMessage({
        id: "tobis.admissions.modalTitle",
        defaultMessage: "Admissions by market",
      })}
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <a
            href={`https://lumiere.obs.coe.int/movie/${admissions.lumiereId}/`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] tracking-[0.1em] text-ink/45 transition hover:text-accent"
          >
            {intl.formatMessage({
              id: "tobis.admissions.source",
              defaultMessage: "LUMIERE film page",
            })}{" "}
            <span aria-hidden="true">↗</span>
          </a>
          <Button variant="secondary" onClick={onClose}>
            {intl.formatMessage({ id: "tobis.admissions.close", defaultMessage: "Close" })}
          </Button>
        </div>
      }
    >
      <div>
        <div className="text-sm text-ink/70">{titleName}</div>
        <div className="mt-1 font-serif text-3xl tabular-nums leading-none">
          {number(admissions.total)}
        </div>
        <div className="mt-2 field-meta text-ink/45">
          {intl.formatMessage(
            {
              id: "tobis.admissions.summary",
              defaultMessage: "{markets, plural, one {# market} other {# markets}}, counted from {year}",
            },
            { markets: admissions.marketCount, year: admissions.totalSinceYear ?? "?" },
          )}
        </div>
      </div>

      <ul className="mt-5 space-y-px">
        {admissions.markets.map((m) => (
          <li
            key={m.market}
            // Der Heimatmarkt bleibt in der Rangfolge, wird aber hinterlegt:
            // die Liste wird nach Zuschauern sortiert, DE steht deshalb je
            // Titel an anderer Stelle und waere sonst jedes Mal zu suchen.
            className={`flex items-baseline justify-between gap-4 rounded px-2 py-1.5 ${
              m.market === "DE" ? "bg-ink/[0.05]" : ""
            }`}
          >
            <div className="min-w-0">
              <div className="field-value text-ink/80">{marketLabel(m.market, intl.locale)}</div>
              {m.distributor || m.releaseDate ? (
                <div className="field-meta truncate text-ink/45">
                  {[
                    m.distributor,
                    m.releaseDate ? intl.formatDate(m.releaseDate, { dateStyle: "medium" }) : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              ) : null}
            </div>
            <div className="shrink-0 text-sm tabular-nums text-ink/80">{number(m.admissions)}</div>
          </li>
        ))}
      </ul>

      {ffaAdmissionsDe ? (
        <p className="mt-5 field-meta text-ink/55">
          {intl.formatMessage(
            {
              id: "tobis.admissions.ffaNote",
              defaultMessage:
                "The FFA hit list reports {count} admissions for Germany - a published settlement, and therefore the better figure for DE.",
            },
            { count: number(ffaAdmissionsDe) },
          )}
        </p>
      ) : null}

      <p className="mt-3 field-meta text-ink/45">
        {intl.formatMessage({
          id: "tobis.admissions.disclaimer",
          defaultMessage:
            "LUMIERE calls its figures minimum estimates; coverage varies by market and year, and outside Europe it only holds what was reported to it. Admissions before the first year of record are missing.",
        })}
      </p>
    </Modal>
  );
}
